import { ContactInfoEditorViewModel } from '@/features/tax-profile/ContactInfoEditorViewModel'
import { BusinessInfoEditorViewModel } from '@/features/tax-profile/business-profiles/BusinessInfoEditorViewModel'
import { action, observable, runInAction, computed } from 'mobx'
import type { BusinessProfileVersion } from '@/features/tax-profile/business-profiles/BusinessProfile'
import { validationContext } from 'validx'
import { validateObject } from '@/utils/validx-util'
import type { BusinessProfileStore } from '@/features/tax-profile/business-profiles/BusinessProfileStore'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { task } from 'mobx-task'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'

export interface BusinessProfileEditorViewModelOptions {
  /**
   * Whether the editor is in read-only mode.
   */
  isReadOnly?: () => boolean

  /**
   * Called when the profile has been saved.
   *
   * @param profile
   */
  onSaved(profile: BusinessProfileVersion): Promise<unknown>
}

/**
 * Editor for a business profile.
 */
export class BusinessProfileEditorViewModel {
  readonly businessInfoVm = new BusinessInfoEditorViewModel()
  readonly contactInfoVm = new ContactInfoEditorViewModel()

  /**
   * Underlying modal state, for when the editor is displayed in a modal.
   */
  readonly modal = new ModalViewModel()

  /**
   * Observable that emits when a business profile is delisted.
   */
  readonly businessProfileDelisted$

  /**
   * Observable that emits when a business profile is added.
   */
  readonly businessProfileAdded$

  /**
   * The profile being edited, if any.
   */
  @observable accessor businessProfile: BusinessProfileVersion | null = null

  @observable accessor isNew = true

  /**
   * Whether the editor is in read-only mode.
   */
  @computed get isReadOnly() {
    return this._options.isReadOnly?.() ?? false
  }

  readonly validation = validationContext(this, {
    businessInfoVm: [validateObject()],
    contactInfoVm: [validateObject()],
  })

  constructor(
    getCurrentMember: GetCurrentMemberFn,
    private readonly _businessProfileStore: BusinessProfileStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _options: BusinessProfileEditorViewModelOptions,
  ) {
    this.businessProfileDelisted$ =
      this._businessProfileStore.businessProfileDelisted$
    this.businessProfileAdded$ =
      this._businessProfileStore.businessProfileVersions.additions$
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  showDialog(businessProfile: BusinessProfileVersion | null) {
    this.reset(businessProfile)
    this.modal.show()
  }

  /**
   * Closes the modal.
   */
  @action.bound
  close() {
    this.modal.close()
  }

  /**
   * Resets the state of the view model to that of the given
   * {@link BusinessProfileVersion}.
   */
  @action.bound
  reset(profile?: BusinessProfileVersion | null) {
    this.isNew = !profile
    this.businessProfile = profile ?? null
    this.businessInfoVm.reset(profile?.businessInfo)
    this.contactInfoVm.reset(profile?.contactInfo)

    this.validation.reset()
  }

  /**
   * Saves the profile and closes the dialog.
   */
  save = task.resolved(async () => {
    this.validation.reset().validate()
    if (!this.validation.isValid) {
      return
    }

    await this._businessProfileStore
      .saveBusinessProfileVersion({
        id: this.businessProfile?.id,
        businessInfo: this.businessInfoVm.toBusinessInfo(),
        contactInfo: this.contactInfoVm.toContactInfo(),
      })
      .then((profile) => {
        runInAction(() => {
          this.businessProfile = profile
        })
        return this._options.onSaved(profile)
      })
      .then(() => this.close())
      .catch(
        this._createSnackbarErrorHandler('Failed to save business profile'),
      )
  })
}
